import { type FC, useState } from 'react';

import CustomTable from 'components/table/CustomTable';
import { SubTitle } from 'components/text/SubTitle';
import { Modal, Tooltip, Typography, formatDate, openModal } from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';

import { ReactComponent as ViewNoteIcon } from '../../../assets/icons/note-32.svg';

import type { LessonResult } from '../core/type';
import type { HeaderTableItem } from 'components/table/type';

import { orderBy, sortBy, sumBy } from 'lodash';

import { LearnerResultStatusCard } from './LearnerResultStatusCard';
import { MixedChart } from './MixedChart';

export const LessonsComp: FC<{ lessonResult: LessonResult[] }> = ({ lessonResult }) => {
  const [comment, setComment] = useState<string>('');
  const [isHidden, setIsHidden] = useState<[boolean, boolean]>([true, false]);

  const header: HeaderTableItem[] = [
    {
      title: '',
      field: 'collapse',
      style: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
    },
    {
      title: 'Lesson',
      field: 'lesson',
      style: {
        width: '65%',
        minWidth: 'calc(65% - 550px)',
      },
    },
    {
      title: 'Score',
      field: 'score',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Status',
      field: 'status',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Completed on',
      field: 'isCompleted',
      style: {
        width: '15%',
        minWidth: 200,
      },
    },
    {
      title: '',
      field: 'viewNote',
      style: {
        width: 60,
        minWidth: 20,
      },
    },
  ];

  const headersChildren: HeaderTableItem[] = [
    {
      title: '#',
      field: 'index',
      style: {
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        textAlign: 'center',
      },
    },
    {
      title: 'Exercise',
      field: 'exercise',
      style: {
        width: '65%',
        minWidth: 'calc(65% - 550px)',
      },
    },
    {
      title: 'Score',
      field: 'score',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Set',
      field: 'set',
      style: {
        width: '10%',
        minWidth: 150,
      },
    },
    {
      title: 'Reps',
      field: 'reps',
      style: {
        width: '15%',
        minWidth: 200,
      },
    },
    {
      title: '',
      field: 'viewNote',
      style: {
        width: 60,
        minWidth: 20,
      },
    },
  ];

  const renderBody = (index: number, row: any, column: HeaderTableItem) => {
    switch (column.field) {
      case 'lesson':
        return (
          <div>
            <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
              {row.lesson.name}
            </Typography>
            <SubTitle
              leftTitle={row.lesson.type.name}
              rightTitle={`${row.items.length} exercises`}
            />
          </div>
        );

      case 'score':
        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.lesson.score ?? 0}/{row.lesson.maxScore}
          </Typography>
        );

      case 'status':
        return <LearnerResultStatusCard status={row.lesson.status} />;

      case 'isCompleted':
        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.lesson.completedOn
              ? formatDate(row.lesson.completedOn, 'iii, d LLL, hh:mm a ')
              : '-'}
          </Typography>
        );
    }
  };

  const renderBodyChildren = (index: number, row: any, column: HeaderTableItem) => {
    switch (column.field) {
      case 'index':
        return <Typography>{index + 1}</Typography>;

      case 'exercise':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={row.exercise.thumbnail} style={{ width: 48, height: 48, borderRadius: 12 }} />
            <div>
              <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                {row.exercise.name}
              </Typography>
              <Typography style={{ fontSize: '16px', lineHeight: '22px', color: '#808080' }}>
                {row.exercise.category.name}
              </Typography>
            </div>
          </div>
        );

      case 'score':
        return (
          <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
            {row.detail ? row.detail.score : 0}/{Number(row.reps) * Number(row.sets)}
          </Typography>
        );

      case 'set':
        return (
          <>
            {row.detail &&
              orderBy(row.detail.userClassScheduleExerciseSets, 'sets').map((el: any) => {
                return (
                  <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                    {el.sets}
                  </Typography>
                );
              })}
          </>
        );

      case 'reps':
        return (
          <>
            {row.detail &&
              orderBy(row.detail.userClassScheduleExerciseSets, 'sets').map((el: any) => {
                return (
                  <Typography style={{ fontSize: '18px', lineHeight: '26px' }}>
                    {el.score}/{el.reps}
                  </Typography>
                );
              })}
          </>
        );

      case 'viewNote':
        return row?.detail?.comment ? (
          <>
            <Tooltip placement="top" arrow={false} content="View note" offset={[0, -4]}>
              <ViewNoteIcon
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setComment(row.detail.comment);
                  openModal(MODAL_KEY.VIEW_NOTE);
                }}
              />
            </Tooltip>
          </>
        ) : null;
    }
  };

  const newLessonResult = sortBy(
    lessonResult.map((el) => ({
      ...el,
      lesson: {
        ...el.lesson,
        score: sumBy(el.items, 'detail.score'),
        maxScore: sumBy(el.items, 'maxScore'),
      },
    })),
    'lesson.completedOn',
  );

  return (
    <div>
      <div className="border-container p-16">
        <MixedChart
          setIsHidden={setIsHidden}
          isHidden={isHidden}
          data={{
            labels: newLessonResult.map((el) =>
              formatDate(String(el.lesson.completedOn), 'yyyy MM dd'),
            ),
            datasets: [
              {
                type: 'line',
                label: '% of max score',
                borderColor: '#49A3F1',
                data: newLessonResult.map((el) => (el.lesson.score / el.lesson.maxScore) * 100),
                backgroundColor: '#49A3F1',
                yAxisID: 'y',
                hidden: isHidden[0],
              },
              {
                type: 'bar',
                label: 'Score',
                data: newLessonResult.map((el) => el.lesson.score),
                barPercentage: 0.2,
                borderRadius: 10,
                yAxisID: 'y1',
                hidden: isHidden[1],
              },
            ],
          }}
        />
      </div>

      <div className="border-container p-16 mt-16">
        <CustomTable
          headers={header}
          bodies={newLessonResult?.map((el, index) => ({
            ...el,
            lesson: {
              ...el.lesson,
              score: sumBy(el.items, 'detail.score'),
              maxScore: sumBy(el.items, 'maxScore'),
            },
            id: index,
          }))}
          renderBody={renderBody}
          headersChildren={headersChildren}
          renderBodyChildren={renderBodyChildren}
        />
      </div>

      <Modal
        header={
          <Typography variant="h4" style={{ fontSize: 22, fontWeight: '500' }}>
            Note
          </Typography>
        }
        closeIconStyle={{
          width: 24,
          height: 24,
        }}
        modalKey={MODAL_KEY.VIEW_NOTE}
        submitButton={{ hide: true }}
        cancelButton={{ hide: true }}
        onCancel={() => {
          setComment('');
        }}
      >
        <Typography variant="body-2" style={{ fontSize: 18 }}>
          {comment}
        </Typography>
      </Modal>
    </div>
  );
};
